/**
 * Utilidades para construir y validar PageFormRequest antes de enviarlo al backend
 * Asegura que el JSON enviado sea coherente y válido
 */

import { LocalizedText } from 'utils/localization';

/** Payload de formulario de componentes en page management (no confundir con `RequestData` global de orgsaas para gráficos). */
export interface PageFormRequest {
  ObjectId: string;
  algorithm?: string;
  algorithmField?: string;
  filters?: Array<{
    fieldName: string;
    operator: string;
    value: string;
  }>;
  orderby?: string;
  groupby?: string;
  title?: LocalizedText; // Soporte multiidioma
  targetValue?: string;
  attributes?: Record<string, any>;
  visibilityRules?: ComponentVisibilityRules;
  allowEdit?: boolean; // Permitir edición de registros en la tabla
  pageSize?: number;
}

export interface ComponentVisibilityRules {
  enabled?: boolean;
  allowedGroups?: string[];
  deniedGroups?: string[];
  requiredUserTypes?: string[];
}

/**
 * Limpia y valida un PageFormRequest antes de enviarlo
 * - Remueve campos vacíos innecesarios
 * - Valida que los campos requeridos estén presentes
 * - Normaliza los filtros
 */
export function cleanRequestData(data: PageFormRequest | null | undefined): PageFormRequest | null {
  if (!data || !data.ObjectId) {
    return null;
  }

  const cleaned: PageFormRequest = {
    ObjectId: data.ObjectId,
  };

  // Solo incluir campos si tienen valores
  if (data.algorithm && data.algorithm.trim()) {
    cleaned.algorithm = data.algorithm.trim();
  }

  if (data.algorithmField && data.algorithmField.trim()) {
    cleaned.algorithmField = data.algorithmField.trim();
  }

  if (data.groupby && data.groupby.trim()) {
    cleaned.groupby = data.groupby.trim();
  }

  if (data.orderby && data.orderby.trim()) {
    cleaned.orderby = data.orderby.trim();
  }

  // Manejar título multiidioma o string simple
  if (data.title) {
    if (typeof data.title === 'string' && data.title.trim()) {
      cleaned.title = data.title.trim();
    } else if (typeof data.title === 'object' && Object.keys(data.title).length > 0) {
      cleaned.title = data.title;
    }
  }

  if (data.targetValue && data.targetValue.trim()) {
    cleaned.targetValue = data.targetValue.trim();
  }

  if (data.pageSize !== undefined) {
    const pageSize = Number(data.pageSize);
    if (Number.isFinite(pageSize) && pageSize > 0) {
      cleaned.pageSize = Math.min(100, Math.max(1, Math.trunc(pageSize)));
    }
  }

  // Limpiar y validar filtros
  if (data.filters && Array.isArray(data.filters) && data.filters.length > 0) {
    const validFilters = data.filters
      .filter(f => f && f.fieldName && f.fieldName.trim() && f.operator && f.operator.trim())
      .map(f => ({
        fieldName: f.fieldName.trim(),
        operator: f.operator.trim(),
        value: f.value != null ? String(f.value) : ''
      }));
    
    if (validFilters.length > 0) {
      cleaned.filters = validFilters;
    }
  }

  // Incluir attributes si existen
  if (data.attributes && Object.keys(data.attributes).length > 0) {
    cleaned.attributes = data.attributes;
  }

  if (data.visibilityRules?.enabled) {
    cleaned.visibilityRules = {
      enabled: true,
      allowedGroups: Array.isArray(data.visibilityRules.allowedGroups) ? data.visibilityRules.allowedGroups : [],
      deniedGroups: Array.isArray(data.visibilityRules.deniedGroups) ? data.visibilityRules.deniedGroups : [],
      requiredUserTypes: Array.isArray(data.visibilityRules.requiredUserTypes) ? data.visibilityRules.requiredUserTypes : [],
    };
  }

  return cleaned;
}

/**
 * Normaliza y limpia requests de componentes antes de consultar.
 * Si no hay ObjectId utilizable, devuelve null para que el componente no dispare la query.
 */
export function prepareComponentQueryRequest(raw: unknown): PageFormRequest | null {
  const normalized = normalizeComponentFormRequest(raw);
  return cleanRequestData(normalized);
}

/**
 * Valida que un PageFormRequest tenga los campos mínimos necesarios
 */
export function validateRequestData(data: PageFormRequest | null): { valid: boolean; error?: string } {
  if (!data) {
    return { valid: false, error: 'RequestData is null or undefined' };
  }

  if (!data.ObjectId || !data.ObjectId.trim()) {
    return { valid: false, error: 'ObjectId is required' };
  }

  // Si hay algoritmo, debe haber algorithmField (excepto count_total con groupby)
  if (data.algorithm && data.algorithm.trim()) {
    const algorithm = data.algorithm.trim();
    const needsField = algorithm !== 'count_total' || !data.groupby;
    
    if (needsField && (!data.algorithmField || !data.algorithmField.trim())) {
      return { valid: false, error: `Algorithm "${algorithm}" requires algorithmField` };
    }
  }

  // Validar filtros
  if (data.filters && Array.isArray(data.filters)) {
    for (const filter of data.filters) {
      if (!filter.fieldName || !filter.fieldName.trim()) {
        return { valid: false, error: 'Filter missing fieldName' };
      }
      if (!filter.operator || !filter.operator.trim()) {
        return { valid: false, error: 'Filter missing operator' };
      }
    }
  }

  return { valid: true };
}

/** Claves permitidas en el nivel raíz de PageFormRequest; el resto se considera configuración de componente → `attributes`. */
const REQUEST_DATA_TOP_LEVEL_KEYS = new Set<string>([
  'ObjectId',
  'algorithm',
  'algorithmField',
  'filters',
  'orderby',
  'groupby',
  'title',
  'targetValue',
  'attributes',
  'visibilityRules',
  'allowEdit',
  'pageSize',
]);

const REQUEST_DATA_WRAPPER_KEYS = ['formRequest', 'requestData', 'data', 'config'] as const;

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

function collectRequestLikeRecords(
  record: Record<string, unknown>,
  out: Record<string, unknown>[],
  seen: Set<Record<string, unknown>>,
  depth: number = 0
) {
  if (!isPlainRecord(record) || seen.has(record) || depth > 4) {
    return;
  }

  seen.add(record);
  out.push(record);

  const containers: Record<string, unknown>[] = [record];
  if (isPlainRecord(record.attributes)) {
    containers.push(record.attributes);
  }

  for (const container of containers) {
    for (const key of REQUEST_DATA_WRAPPER_KEYS) {
      const candidate = container[key];
      if (isPlainRecord(candidate)) {
        collectRequestLikeRecords(candidate, out, seen, depth + 1);
      }
    }
  }
}

/**
 * Algunos `formRequest` guardados (o antiguos) dejan Kanban, Items, QR, etc. en la raíz del JSON.
 * Los formularios de page management leen casi todo desde `attributes`, así que aquí unificamos.
 */
export function normalizeComponentFormRequest(raw: unknown): PageFormRequest | null {
  if (!isPlainRecord(raw)) {
    return null;
  }

  const src = raw as Record<string, unknown>;
  const requestLikeRecords: Record<string, unknown>[] = [];
  collectRequestLikeRecords(src, requestLikeRecords, new Set());

  const nestedAttrs: Record<string, unknown> = {};
  for (const record of requestLikeRecords) {
    if (isPlainRecord(record.attributes)) {
      Object.assign(nestedAttrs, record.attributes as Record<string, unknown>);
    }
  }

  const result: Record<string, unknown> = {};

  for (const key of REQUEST_DATA_TOP_LEVEL_KEYS) {
    if (key === 'attributes') continue;
    for (const record of requestLikeRecords) {
      if (record[key] !== undefined) {
        result[key] = record[key];
        break;
      }
    }
  }

  for (const record of requestLikeRecords) {
    for (const [k, v] of Object.entries(record)) {
      if (REQUEST_DATA_TOP_LEVEL_KEYS.has(k)) continue;
      if (REQUEST_DATA_WRAPPER_KEYS.includes(k as typeof REQUEST_DATA_WRAPPER_KEYS[number])) continue;
      if (v === undefined) continue;
      const cur = nestedAttrs[k];
      const curEmpty = cur === undefined || cur === null || cur === '';
      if (curEmpty) {
        nestedAttrs[k] = v;
      }
    }
  }

  const attrs = nestedAttrs as Record<string, any>;

  const firstDefinedStr = (...vals: unknown[]): string => {
    for (const v of vals) {
      if (v === null || v === undefined) continue;
      const s = String(v).trim();
      if (s !== '') return s;
    }
    return '';
  };

  const objectId = firstDefinedStr(
    result.ObjectId,
    src.objectId,
    src.ObjectID,
    src.object_id,
    ...requestLikeRecords.map((record) => record.objectId),
    ...requestLikeRecords.map((record) => record.ObjectID),
    ...requestLikeRecords.map((record) => record.object_id),
    attrs.ObjectId,
    attrs.objectId,
    attrs.ObjectID,
    attrs.object_id,
    attrs.selectedObjectId,
    attrs.ordersObjectId,
    attrs.reservationsObjectId,
    attrs.reservationObjectId,
    attrs.QRCodeObject
  );

  const out: PageFormRequest = {
    ObjectId: objectId,
    attributes: attrs,
  };

  const algorithm = firstDefinedStr(result.algorithm, nestedAttrs.algorithm);
  if (algorithm) out.algorithm = algorithm;

  const algorithmField = firstDefinedStr(result.algorithmField, nestedAttrs.algorithmField);
  if (algorithmField) out.algorithmField = algorithmField;

  const orderby = firstDefinedStr(result.orderby, nestedAttrs.orderby);
  if (orderby) out.orderby = orderby;

  const groupby = firstDefinedStr(result.groupby, nestedAttrs.groupby);
  if (groupby) out.groupby = groupby;

  const targetValue = firstDefinedStr(result.targetValue, nestedAttrs.targetValue);
  if (targetValue) out.targetValue = targetValue;

  const titleVal = result.title !== undefined ? result.title : nestedAttrs.title;
  if (titleVal !== undefined && titleVal !== null && titleVal !== '') {
    if (typeof titleVal === 'string' && titleVal.trim()) {
      out.title = titleVal.trim();
    } else if (typeof titleVal === 'object' && !Array.isArray(titleVal) && Object.keys(titleVal as object).length > 0) {
      out.title = titleVal as LocalizedText;
    }
  }

  const filtersRaw = result.filters !== undefined ? result.filters : nestedAttrs.filters;
  if (Array.isArray(filtersRaw) && filtersRaw.length > 0) {
    out.filters = filtersRaw
      .filter((f): f is Record<string, unknown> => Boolean(f) && typeof f === 'object' && !Array.isArray(f))
      .map((f) => ({
        fieldName: String(f.fieldName ?? ''),
        operator: String(f.operator ?? ''),
        value: f.value != null ? String(f.value) : '',
      }));
  }

  if (result.allowEdit === true || nestedAttrs.allowEdit === true) {
    out.allowEdit = true;
  }

  const pageSizeRaw = result.pageSize !== undefined ? result.pageSize : nestedAttrs.pageSize;
  if (pageSizeRaw !== undefined && pageSizeRaw !== null && pageSizeRaw !== '') {
    const pageSize = Number(pageSizeRaw);
    if (Number.isFinite(pageSize) && pageSize > 0) {
      out.pageSize = Math.min(100, Math.max(1, Math.trunc(pageSize)));
    }
  }

  const visibilityRaw = result.visibilityRules !== undefined ? result.visibilityRules : nestedAttrs.visibilityRules;
  if (isPlainRecord(visibilityRaw)) {
    out.visibilityRules = {
      enabled: visibilityRaw.enabled === true,
      allowedGroups: Array.isArray(visibilityRaw.allowedGroups)
        ? visibilityRaw.allowedGroups.map((value) => String(value)).filter(Boolean)
        : [],
      deniedGroups: Array.isArray(visibilityRaw.deniedGroups)
        ? visibilityRaw.deniedGroups.map((value) => String(value)).filter(Boolean)
        : [],
      requiredUserTypes: Array.isArray(visibilityRaw.requiredUserTypes)
        ? visibilityRaw.requiredUserTypes.map((value) => String(value)).filter(Boolean)
        : [],
    };
  }

  return out;
}

/**
 * Normaliza el `formRequest` antes de persistirlo en Page.
 * Mantiene la forma objeto esperada por el backend y asegura `attributes`
 * aunque el componente no tenga un `ObjectId` consultable.
 */
export function normalizePersistedComponentFormRequest(raw: unknown): PageFormRequest | Record<string, any> | null {
  if (!isPlainRecord(raw)) {
    return null;
  }

  const normalized = normalizeComponentFormRequest(raw);
  if (normalized) {
    return {
      ...normalized,
      attributes: isPlainRecord(normalized.attributes) ? normalized.attributes : {},
    };
  }

  return {
    ...raw,
    attributes: isPlainRecord((raw as Record<string, unknown>).attributes)
      ? ((raw as Record<string, unknown>).attributes as Record<string, unknown>)
      : {},
  };
}
